import {FC, JSX, MouseEventHandler, useCallback, useMemo} from 'react';
import {styled, useTheme} from 'styled-components';

import {asNumber} from '@shared/lib/utils/type_utils';

import {createButton} from '@shared_web/components/core/button';
import {EmptyFragment} from '@shared_web/lib/react';
import {optional} from '@shared_web/lib/styled_utils';
import {background, borderColor, textColor} from '@shared_web/theme/theme_utils';

interface TimePickerProps {
  header?: JSX.Element | string;
  onClick: (hour: number, minute: number) => void;
  hours?: number[];
  minutes?: number[];
  denseUi?: boolean;
  utc?: boolean;
}

function buttonText(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

const DEFAULT_HOURS = [
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
];

const DEFAULT_MINUTES = [0];

export const TimePicker: FC<TimePickerProps> = props => {
  const {
    header,
    onClick,
    hours = DEFAULT_HOURS,
    minutes = DEFAULT_MINUTES,
    denseUi = false,
  } = props;
  const {button} = useTheme();

  const handleClick = useCallback<MouseEventHandler>(
    evt => {
      const hour = asNumber(evt.currentTarget.getAttribute('data-hour'), 0);
      const minute = asNumber(evt.currentTarget.getAttribute('data-minute'), 0);
      onClick(hour, minute);
    },
    [onClick]
  );

  const TimeButton = useMemo(() => {
    return createButton({
      type: 'button',
      themeType: 'button',
      themeOverrides: {
        paddingTop: denseUi ? 4 : 8,
        paddingRight: denseUi ? 6 : 10,
        paddingBottom: denseUi ? 4 : 8,
        paddingLeft: denseUi ? 6 : 10,
        ...background('#00000004'),
        backgroundColorHover: '#ffffff',
        ...textColor('#444444'),
        textColorHover: button?.backgroundColorActive,
        borderWidth: 2,
        ...borderColor(`${button?.backgroundColorActive}88`),
        borderColorHover: button?.backgroundColorActive,
        fontSize: denseUi ? 15 : 17,
        fontWeight: 'regular',
      },
    });
  }, [button?.backgroundColorActive, denseUi]);

  return (
    <Wrapper $denseUi={denseUi}>
      {header === undefined ? (
        EmptyFragment
      ) : typeof header === 'string' ? (
        <Title $denseUi={denseUi}>{header}</Title>
      ) : (
        header
      )}
      <ButtonsWrapper $denseUi={denseUi}>
        {hours.flatMap(hour => {
          return minutes.map(minute => {
            const text = buttonText(hour, minute);
            return (
              <TimeButton data-hour={hour} data-minute={minute} key={text} onClick={handleClick}>
                {text}
              </TimeButton>
            );
          });
        })}
      </ButtonsWrapper>
    </Wrapper>
  );
};
TimePicker.displayName = 'TimePicker';

const Wrapper = styled.div<{$denseUi: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${p => (p.$denseUi ? '8px 4px' : '16px')};
  ${p => optional('color', p.theme.main?.textColor)}
`;

const Title = styled.div<{$denseUi: boolean}>`
  text-align: center;
  font-size: ${p => (p.$denseUi ? '15px' : '16px')};
  margin-bottom: ${p => (p.$denseUi ? 3 : 16)}px;
`;

const ButtonsWrapper = styled.div<{$denseUi: boolean}>`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  font-size: ${p => (p.$denseUi ? 12 : 18)}px;
  grid-gap: 10px 3px;
`;
